import {
  getHeaderImage,
  BRANDTV_LOGO,
  formatDateParts,
  eventTitle as getEventTitle,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  getSessionDateLabel,
  getSessionTimeLabel,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import ButtonWidget from './widgets/button-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import { templateStaticDefaults } from './template-defaults';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';
import { BLOCK_ID } from './widgets/block-ids';
import { DateTimeLayout } from './widgets/date-time-layout';

const DEFAULTS = templateStaticDefaults.astoria;

const ASTORIA_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.headerLogo]: { height: 32, padding: '20px' },
  [BLOCK_ID.salutation]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '32px',
    fontWeight: 400,
    letterSpacing: '0.96px',
    lineHeight: '38px',
    textAlign: 'center',
    padding: '32px 56px',
    background: 'var(--container)',
  },
  [BLOCK_ID.mainImage]: {
    size: 'original',
    width: 248,
    height: 325,
    padding: '0 56px',
    borderRadius: 'var(--image-radius)',
    background: 'var(--container)',
  },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '48px',
    fontWeight: 400,
    letterSpacing: '1.44px',
    textTransform: 'uppercase',
    lineHeight: '52px',
    textAlign: 'center',
    padding: '32px 56px',
    background: 'var(--container)',
  },
  [BLOCK_ID.description]: {
    color: 'var(--body)',
    fontFamily: 'var(--body-font)',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '16.8px',
    textAlign: 'center',
    padding: '32px 64px',
  },
  [BLOCK_ID.cta]: {
    size: 'small',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--body-font)',
    fontWeight: 700,
  },
  [BLOCK_ID.divider]: { color: 'var(--container)', padding: '0 56px' },
  [BLOCK_ID.legalText]: {
    color: '#8F8F8F',
    fontFamily: 'var(--body-font)',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '18px',
    textAlign: 'center',
    padding: '32px 64px',
  },
  // the date & time block keeps astoria's original compact day/time pair
  [BLOCK_ID.dateTimeCard]: {
    layout: DateTimeLayout.Compact,
    padding: '12px 56px',
    headingColor: 'var(--body)',
    longDayName: true,
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Astoria = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const secondaryAccent = resolveThemedColor(
    customizations?.secondaryAccentColor,
    themeColors?.secondaryAccentColor,
    DEFAULTS.secondaryAccentColor
  );
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '0px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || BRANDTV_LOGO;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Playfair Display", Georgia, serif'
  );
  const bodyFont = resolveThemedFont(customizations?.bodyFont, theme?.font_pack?.body_name, 'Lato, Arial, sans-serif');
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventTitle = title || customizations?.title || getEventTitle(event, 'en');
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = DEFAULTS.bodyBackgroundColor;
  const footerTextColor = '#A0A0A0';
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryAccent,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: ASTORIA_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Astoria"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--secondary-accent': secondaryAccent,
            '--body': body,
            '--heading': heading,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  <tr>
                    <td style={{ background: bodyColor }}>
                      {blocks.map((block) => {
                        if (block.id === BLOCK_ID.description && !eventDescription) return null;

                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={block}
                              stylePreset={ASTORIA_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Astoria"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--secondary-accent': secondaryAccent,
          '--body': body,
          '--heading': heading,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px', background: bodyColor }}
            >
              <tbody>
                <tr>
                  <td>
                    <LogoWidget src={headerImg} setImage={setHeaderImage} height={32} padding="20px" />
                  </td>
                </tr>

                <tr>
                  <td>
                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="32px"
                      fontWeight={400}
                      letterSpacing="0.96px"
                      lineHeight="38px"
                      textAlign="center"
                      padding="32px 56px"
                      background="var(--container)"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      size="original"
                      width={248}
                      height={325}
                      padding="0 56px"
                      borderRadius={imageRadius}
                      background="var(--container)"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="48px"
                      fontWeight={400}
                      letterSpacing="1.44px"
                      textTransform="uppercase"
                      lineHeight="52px"
                      textAlign="center"
                      padding="32px 56px"
                      background="var(--container)"
                    />
                  </td>
                </tr>

                {eventDescription && (
                  <tr>
                    <td>
                      <ParagraphWidget
                        value={eventDescription}
                        setValue={setDescription}
                        color="var(--body)"
                        fontFamily="var(--body-font)"
                        fontSize="14px"
                        fontWeight={400}
                        lineHeight="16.8px"
                        textAlign="center"
                        padding="32px 64px"
                      />
                    </td>
                  </tr>
                )}

                {sessionDateParts && (
                  <tr>
                    <td align="center" style={{ padding: '12px 56px' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td align="center" style={{ padding: '16px 24px' }}>
                              <div
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '14px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 500,
                                  letterSpacing: '0.42px',
                                  textTransform: 'uppercase',
                                  textAlign: 'center',
                                }}
                              >
                                {sessionDateParts.dayName}
                              </div>
                              <div
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '32px',
                                  fontFamily: 'var(--heading-font)',
                                  textAlign: 'center',
                                }}
                              >
                                {sessionDateParts.shortDate}
                              </div>
                            </td>
                            <td
                              align="center"
                              valign="middle"
                              style={{ color: 'var(--body)', fontSize: '24px', fontFamily: 'var(--body-font)' }}
                            >
                              —
                            </td>
                            <td align="center" style={{ padding: '16px 24px' }}>
                              <div
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '14px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 500,
                                  letterSpacing: '0.42px',
                                  textTransform: 'uppercase',
                                  textAlign: 'center',
                                }}
                              >
                                {sessionDateParts.period}
                              </div>
                              <div
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '32px',
                                  fontFamily: 'var(--heading-font)',
                                  textAlign: 'center',
                                }}
                              >
                                {sessionDateParts.time}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}

                <tr>
                  <td>
                    <ButtonWidget
                      value={_cta}
                      setValue={setCta}
                      href={anchorHref}
                      size="small"
                      color="var(--button-text)"
                      buttonColor="var(--button-color)"
                      fontFamily="var(--body-font)"
                      fontWeight={700}
                      borderRadius={buttonRadius}
                    />
                  </td>
                </tr>

                <tr>
                  <td style={{ padding: '0 56px' }}>
                    <div style={{ borderTop: '1px solid var(--accent)', fontSize: 0, lineHeight: '0' }}>&nbsp;</div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <ParagraphWidget
                      value={DEFAULTS.complianceText}
                      color="#8F8F8F"
                      fontFamily="var(--body-font)"
                      fontSize="12px"
                      fontWeight={400}
                      lineHeight="18px"
                      textAlign="center"
                      padding="32px 64px"
                    />
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
